import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  canAccessUserManagement,
  canAccessPropertySetup,
  canManageAnnouncements,
  canAccessAnalytics,
  getRoleLabel } from
'../../utils/roleCheck';
import {
  LuLayoutDashboard,
  LuMessageSquare,
  LuBell,
  LuUser,
  LuUsers,
  LuMegaphone,
  LuSettings,
  LuX,
  LuBuilding2,
  LuMap,
  LuTrendingUp } from
'react-icons/lu';
import { FiAlertTriangle } from 'react-icons/fi';

export default function Sidebar({ isOpen, onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Base navigation items (available to all users)
  const navItems = [
  { to: '/dashboard', icon: LuLayoutDashboard, label: t('sidebar.dashboard') },
  { to: '/announcements', icon: LuMegaphone, label: t('sidebar.announcements') },
  { to: '/fault-reports', icon: FiAlertTriangle, label: t('sidebar.faultReports') },
  { to: '/forum', icon: LuMessageSquare, label: t('sidebar.forum') },
  { to: '/notifications', icon: LuBell, label: t('sidebar.notifications') },
  { to: '/settings', icon: LuSettings, label: t('sidebar.settings') },
  { to: '/profile', icon: LuUser, label: t('sidebar.profile') }];



  // Management items (role-based)
  const managementItems = [];

  if (canAccessPropertySetup(user?.role)) {
    managementItems.push({ to: '/property-setup', icon: LuMap, label: t('sidebar.infrastructure') });
  }

  if (canAccessUserManagement(user?.role)) {
    managementItems.push({ to: '/user-management', icon: LuUsers, label: t('sidebar.userManagement') });
  }

  if (canAccessAnalytics(user?.role)) {
    managementItems.push({ to: '/analytics', icon: LuTrendingUp, label: t('sidebar.analytics') });
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen &&
      <div
        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        onClick={onClose} />

      }

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen bg-white border-r border-gray-200 w-64 z-50 transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <LuBuilding2 className="w-6 h-6 text-blue-600" />
              <h2 className="font-bold text-lg text-gray-900">{t('sidebar.appTitle')}</h2>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition">

              <LuX className="w-5 h-5" />
            </button>
          </div>

          {/* User Role Badge */}
          {user?.role &&
          <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200">
              <div className="text-xs text-gray-600 mb-1">{t('sidebar.signedInAs')}</div>
              <div className="font-bold text-sm text-gray-900">{getRoleLabel(user.role)}</div>
            </div>
          }

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {navItems.map((item) =>
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => onClose()}
              className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-lg transition ${
              isActive ?
              'bg-blue-50 text-blue-600 font-medium' :
              'text-gray-700 hover:bg-gray-50'}`

              }>

                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            )}

            {managementItems.length > 0 &&
            <>
                <div className="pt-4 pb-2 px-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('sidebar.management')}
                  </div>
                </div>
                {managementItems.map((item) =>
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => onClose()}
                className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition ${
                isActive ?
                'bg-blue-50 text-blue-600 font-medium' :
                'text-gray-700 hover:bg-gray-50'}`

                }>

                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </NavLink>
              )}
              </>
            }
          </nav>
        </div>
      </aside>
    </>);

}